import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  MixtureCalculationDto,
  ReportCalculationDto,
  ReportsControllerService
} from '@mobiletract/kobize-service-angular-client';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom } from 'rxjs';

import { CompanyContextService } from '../../core/services/company-context.service';
import { ReportCalculationSummaryComponent } from './fill/report-calculation-summary.component';
import { ReportMixtureCardDialogComponent } from './fill/report-mixture-card-dialog.component';
import { ReportMixtureCalculationCardComponent } from './fill/report-mixture-calculation-card.component';

@Component({
  selector: 'app-report-fill',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    BreadcrumbModule,
    ButtonModule,
    SkeletonModule,
    ToastModule,
    ReportMixtureCalculationCardComponent,
    ReportMixtureCardDialogComponent,
    ReportCalculationSummaryComponent
  ],
  templateUrl: './report-fill.component.html',
  styleUrl: './report-fill.component.css'
})
export class ReportFillComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly reportsApi = inject(ReportsControllerService);
  readonly companyContext = inject(CompanyContextService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  breadcrumbItems: MenuItem[] = [];
  calculation: ReportCalculationDto | null = null;
  /** true until first load completes (title skeleton). */
  isLoading = true;
  loadErrorKey: string | null = null;

  /** Last report id used for calculate (for mixture dialog + reload). */
  fillReportId: string | null = null;
  mixtureDialogOpen = false;
  mixtureDialogMixture: MixtureCalculationDto | null = null;

  ngOnInit(): void {
    this.rebuildBreadcrumb();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.rebuildBreadcrumb();
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      void this.loadCalculationForReportId(params.get('reportId'));
    });
  }

  openMixtureDialogCreate(): void {
    this.mixtureDialogMixture = null;
    this.mixtureDialogOpen = true;
  }

  openMixtureDialogEdit(mixture: MixtureCalculationDto): void {
    this.mixtureDialogMixture = mixture;
    this.mixtureDialogOpen = true;
  }

  onMixtureDialogVisibleChange(open: boolean): void {
    this.mixtureDialogOpen = open;
    if (!open) {
      this.mixtureDialogMixture = null;
    }
  }

  async onMixtureDialogSaved(): Promise<void> {
    await this.loadCalculationForReportId(this.fillReportId);
    if (!this.loadErrorKey && this.calculation) {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('reports.toast.successTitle'),
        detail: this.translate.instant('reports.fill.mixtureDialog.saveSuccess')
      });
    }
  }

  async onMixtureDeleted(): Promise<void> {
    await this.loadCalculationForReportId(this.fillReportId);
    if (!this.loadErrorKey && this.calculation) {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('reports.toast.successTitle'),
        detail: this.translate.instant('reports.fill.mixtureDeleteSuccess')
      });
    }
  }

  async onMixtureChanged(): Promise<void> {
    await this.loadCalculationForReportId(this.fillReportId);
  }

  private rebuildBreadcrumb(): void {
    this.breadcrumbItems = [
      {
        label: this.translate.instant('reports.fill.breadcrumbReports'),
        routerLink: '/reports'
      },
      {
        label: this.translate.instant('reports.fill.breadcrumbFill')
      }
    ];
  }

  private async loadCalculationForReportId(reportId: string | null): Promise<void> {
    this.fillReportId = reportId;
    this.calculation = null;
    this.loadErrorKey = null;
    this.isLoading = true;

    if (!reportId) {
      this.isLoading = false;
      this.loadErrorKey = 'reports.fill.invalidId';
      return;
    }

    try {
      await this.companyContext.loadCompanies();
    } catch {
      this.isLoading = false;
      this.loadErrorKey = 'reports.fill.companiesLoadFailed';
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail: this.translate.instant('reports.fill.companiesLoadFailed')
      });
      return;
    }

    const companyId = this.companyContext.currentCompanyId;
    if (!companyId) {
      this.isLoading = false;
      this.loadErrorKey = 'reports.fill.noCompanies';
      return;
    }

    try {
      const result = await firstValueFrom(this.reportsApi.calculateReport(reportId, companyId));
      this.calculation = result;
      const reportErrors = result.errors ?? [];
      if (reportErrors.length > 0) {
        const detail = reportErrors.map((e) => e.message).join('; ');
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('reports.fill.calculationWarningsTitle'),
          detail
        });
      }
    } catch (err: unknown) {
      const detail =
        this.mapHttpErrorDetail(err) ?? this.translate.instant('reports.fill.calculateFailed');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
      this.loadErrorKey = 'reports.fill.calculateFailed';
    } finally {
      this.isLoading = false;
    }
  }

  private mapHttpErrorDetail(err: unknown): string {
    const fallback = this.translate.instant('common.somethingWentWrong');
    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }
    const body = err.error;
    if (body && typeof body === 'object') {
      const msg = (body as Record<string, unknown>)['message'];
      if (typeof msg === 'string' && msg.trim().length > 0) {
        return msg.trim();
      }
      return fallback;
    }
    if (typeof body === 'string') {
      const text = body.trim();
      if (text.length === 0) {
        return fallback;
      }
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const msg = parsed['message'];
        if (typeof msg === 'string' && msg.trim().length > 0) {
          return msg.trim();
        }
      } catch {
        return fallback;
      }
    }
    return fallback;
  }
}
